import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { Spinner } from '../../../../../components/Spinner';
import { useAuth } from '../../../../../hooks/useAuth';
import { useEnv } from '../../../../../context/env-context';
import { makeRequest } from '../../../../../utils/makeRequest';
import { toast } from 'react-toastify';
import { NumericFormat } from 'react-number-format';
import classNames from 'classnames';
import NewFundingLatestRepaymentDateForm from '../forms/NewFundingLatestRepaymentDateForm';
import NewFundingNextRepaymentDateForm from '../forms/NewFundingNextRepaymentDateForm';

function NewFundingRepaymentsWidget({
  consumerId,
  consumerDefaultPaymentMethod,
  consumerAccountDwollaFundingSourceUrl,
  fundingId,
  fundingCurrentAmount,
  fundingRepaymentMode,
  fundingNextRepaymentDate,
  fundingMaxRepaymentDate,
  fundingActualRepaymentTrials,
  fundingRescheduleCounter,
  fundingRepayments,
  refreshFundingData,
}) {
  const { getAccessTokenSilently } = useAuth();
  const { apiOriginFunding, apiOriginConsumer } = useEnv();

  const [spinner, setSpinner] = useState(false);

  const [consumerStatus, setConsumerStatus] = useState(null);

  const [isManuaRepaymentOpen, setIsManualRepaymentOpen] = useState(false);

  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState(null);

  const achEnabled = consumerAccountDwollaFundingSourceUrl;
  const cardEnabled = consumerStatus?.mainCardUsage?.includes('PULL');

  const handleToggleManualRepayment = () => {
    setIsManualRepaymentOpen(prev => !prev);
    setAmount('');
  };

  const getConsumerStatus = useCallback(async () => {
    setSpinner(true);

    const token = await getAccessTokenSilently();

    if (!token) {
      return;
    }

    const config = { token, url: `${apiOriginConsumer}/get_status`, params: { consumerId } };

    try {
      const response = await makeRequest(config);

      setConsumerStatus(response);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSpinner(false);
    }
  }, [getAccessTokenSilently, apiOriginConsumer, consumerId]);

  useEffect(() => {
    if (isManuaRepaymentOpen) {
      getConsumerStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isManuaRepaymentOpen]);

  useEffect(() => {
    if (achEnabled || cardEnabled) {
      if (achEnabled && cardEnabled) {
        setPaymentMethod(consumerDefaultPaymentMethod);
      } else if (achEnabled) {
        setPaymentMethod('ACH');
      } else {
        setPaymentMethod('CARD');
      }
    }
  }, [achEnabled, cardEnabled, consumerDefaultPaymentMethod]);

  const repayFunding = async () => {
    setSpinner(true);

    const token = await getAccessTokenSilently();

    if (!token) {
      return;
    }

    const config = {
      token,
      url: `${apiOriginFunding}/manual_repay`,
      method: 'POST',
      data: JSON.stringify({ fundingId, amount: parseInt(amount, 10), paymentMethod }),
    };

    try {
      const response = await makeRequest(config);

      await refreshFundingData();

      if (response.status === 'CREATED') {
        toast.success('Payment initiated');
      }

      if (response.status === 'COMPLETED') {
        toast.success('Payment completed successfully');
      }

      if (response.status === 'FAILED') {
        toast.error(`Payment failed! Reason: ${response.failureReason}`);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSpinner(false);
      handleToggleManualRepayment();
    }
  };

  return (
    <div className="bo-border-container bg-light rounded w-100 p-0">
      <div className="p-2">
        <div className="d-flex justify-content-between pb-1">
          <p className="bo-common-text-bold">Repayments</p>
          <OverlayTrigger
            trigger="click"
            placement="bottom-start"
            show={isManuaRepaymentOpen}
            popperConfig={{
              modifiers: [{ name: 'offset', options: { offset: [0, 4] } }],
            }}
            overlay={
              <Popover className="custom-popover">
                <Popover.Body className="bo-common-popover-form-menu">
                  <Spinner visible={spinner} size={36}>
                    <Form.Label className="bo-common-popover-form-menu-label">Amount</Form.Label>
                    <NumericFormat
                      className="bo-common-popover-form-menu-input bo-w-150"
                      value={amount}
                      customInput={Form.Control}
                      onChange={e => setAmount(e.target.value)}
                      allowNegative={false}
                      decimalScale={2}
                    />
                    <Form.Label className="bo-common-popover-form-menu-label mt-2">
                      Payment Method
                    </Form.Label>
                    <div className="d-flex align-items-center">
                      <Form.Check
                        type="radio"
                        value="ACH"
                        checked={paymentMethod === 'ACH'}
                        disabled={!achEnabled}
                        onChange={() => setPaymentMethod('ACH')}
                      />
                      <Form.Label className="bo-common-text">ACH</Form.Label>
                    </div>
                    <div className="d-flex align-items-center">
                      <Form.Check
                        type="radio"
                        value="CARD"
                        checked={paymentMethod === 'CARD'}
                        disabled={!cardEnabled}
                        onChange={() => setPaymentMethod('CARD')}
                      />
                      <Form.Label className="bo-common-text">CARD</Form.Label>
                    </div>
                    <div className="d-flex gap-3" />
                    <div className="mt-3 d-flex justify-content-between">
                      <Button
                        className="bo-common-button"
                        onClick={repayFunding}
                        disabled={
                          spinner || !amount || Number(amount) > Number(fundingCurrentAmount)
                        }
                      >
                        Repay
                      </Button>
                      <Button className="bo-common-button" onClick={handleToggleManualRepayment}>
                        Close
                      </Button>
                    </div>
                  </Spinner>
                </Popover.Body>
              </Popover>
            }
          >
            <Button className="bo-common-button" onClick={handleToggleManualRepayment}>
              Manual Repayment
            </Button>
          </OverlayTrigger>
        </div>
        <div
          className={classNames(
            'd-flex justify-content-between',
            (fundingRepaymentMode === 'CUSTOM' || fundingRepaymentMode === 'FORCE') && 'opacity-50',
          )}
        >
          <p className="bo-common-text">Next Repayment Date</p>
          <NewFundingNextRepaymentDateForm
            fundingId={fundingId}
            fundingNextRepaymentDate={fundingNextRepaymentDate || undefined}
            fundingRepaymentMode={fundingRepaymentMode}
            refreshFundingData={refreshFundingData}
          />
        </div>
        <div className="d-flex justify-content-between">
          <p className="bo-common-text">Latest Repayment Date</p>
          <NewFundingLatestRepaymentDateForm
            fundingId={fundingId}
            fundingMaxRepaymentDate={fundingMaxRepaymentDate || undefined}
            refreshFundingData={refreshFundingData}
          />
        </div>
        <hr className="my-2" />
        <div className="d-flex justify-content-between pb-1">
          <p className="bo-common-text">Repayment trials</p>
          <p className="bo-common-text">{fundingActualRepaymentTrials}</p>
        </div>
        <div className="d-flex justify-content-between">
          <p className="bo-common-text">System reschedules</p>
          <p className="bo-common-text">{fundingRescheduleCounter}</p>
        </div>
        {fundingRepayments.length > 0 && (
          <>
            <hr className="my-2" />
            <div className="d-flex justify-content-between">
              <p className="bo-common-text">Repayments</p>
              <div className="d-flex flex-column align-items-end">
                {fundingRepayments.map(x => (
                  <p key={x.repaymentId} className="bo-common-text">
                    {`$${x.amount} on ${moment(x.timestamp).format('YYYY-MM-DD')}`}
                  </p>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default NewFundingRepaymentsWidget;
